import { apiClient } from './apiClient';

const READING_PROGRESS_API = '/api/app/reading-progress-extn';

export interface ReadingProgressDto {
  id: string;
  bookId: string;
  userId: string;
  currentLocation: string;
  currentLocationLabel: string;
  progressPercentage: number;
  totalReadingTimeSeconds: number;
  lastReadAt: string;
  concurrencyStamp?: string;
}

export interface ReadingProgressUpdateDto {
  currentLocation: string;
  currentLocationLabel: string;
  progressPercentage: number;
  totalReadingTimeSeconds: number;
  lastReadAt: string;
  bookId: string;
  userId: string;
  concurrencyStamp: string;
}

export const readingProgressService = {
  /**
   * Get reading progress for current user and specific book
   * Auto-creates if doesn't exist
   */
  async getMyProgress(bookId: string): Promise<ReadingProgressDto> {
    const response = await apiClient.get<ReadingProgressDto>(
      `${READING_PROGRESS_API}/my-progress`,
      { params: { bookId } }
    );
    return response.data;
  },

  /**
   * Get all existing reading progress records for the current user.
   * Does NOT auto-create records for books that have never been opened.
   */
  async getAllMyProgress(): Promise<ReadingProgressDto[]> {
    const response = await apiClient.get<ReadingProgressDto[]>(
      `${READING_PROGRESS_API}/my-all-progress`
    );
    return response.data;
  },

  /**
   * Update reading progress for current user
   */
  async updateMyProgress(
    bookId: string,
    input: Omit<ReadingProgressUpdateDto, 'concurrencyStamp' | 'bookId' | 'userId' | 'lastReadAt'>
  ): Promise<ReadingProgressDto> {
    // Fetch current progress to get concurrencyStamp
    const currentProgress = await this.getMyProgress(bookId);

    // Merge with update data
    const updateDto: ReadingProgressUpdateDto = {
      ...input,
      bookId: currentProgress.bookId,
      userId: currentProgress.userId,
      lastReadAt: new Date().toISOString(),
      concurrencyStamp: currentProgress.concurrencyStamp || '',
    };

    const response = await apiClient.put<ReadingProgressDto>(
      `${READING_PROGRESS_API}/my-progress`,
      updateDto,
      { params: { bookId } }
    );
    return response.data;
  },

  /**
   * Get total reading time for a book
   */
  async getTotalReadingTime(bookId: string): Promise<number> {
    const response = await apiClient.get<number>(
      `${READING_PROGRESS_API}/total-reading-time`,
      { params: { bookId } }
    );
    return response.data;
  },
};
